'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';
import type { IconType } from 'react-icons';
import { FaChevronRight, FaCaretDown, FaCaretUp } from 'react-icons/fa6';

import { LayoutPageHeader } from '../../../../_components/LayoutPageHeader/LayoutPageHeader';
import { mergeClassNames } from '../../../../_utils/mergeClassNames';
import './headerChrome.scss';

const cardFooterClassName =
  'mt-auto flex min-h-11 w-full shrink-0 cursor-pointer appearance-none flex-row items-center justify-start gap-2 border-0 bg-blue-500/10 px-4 py-3 text-left font-[inherit] text-base font-bold leading-5 text-blue-500 no-underline transition-colors hover:bg-blue-500/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-500';

const cardFooterDisabledClassName =
  'mt-auto flex min-h-11 w-full shrink-0 cursor-not-allowed appearance-none flex-row items-center justify-start gap-2 border-0 bg-white/5 px-4 py-3 text-left font-[inherit] text-base font-bold leading-5 text-bzgray-600';

const cardRowClassName =
  'box-border grid h-12 min-h-12 shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 py-1';

const emptyTitleClassName = 'm-0 text-[15px] font-bold leading-[1.3] text-[#F2F8FF]';
const emptyCopyClassName = 'm-0 line-clamp-3 text-sm font-normal leading-normal text-bzgray-500';

export function HeaderChrome({ children }: { children: ReactNode }): ReactNode {
  return <LayoutPageHeader className="header-chrome-shell">{children}</LayoutPageHeader>;
}

export function HeaderPageTitle({
  as: Heading = 'h1',
  children,
}: {
  as?: 'h1' | 'h2';
  children: ReactNode;
}): ReactNode {
  return <Heading className="header-page-title">{children}</Heading>;
}

export function HeaderOverviewCards({ children, className }: { children: ReactNode; className?: string }): ReactNode {
  return (
    <div className={mergeClassNames('grid grid-cols-1 items-stretch gap-3 lg:grid-cols-3 lg:gap-4', className)}>
      {children}
    </div>
  );
}

export function HeaderCard({
  children,
  footer,
  footerDisabled = false,
  footerHref,
  isLoadingFooter = false,
  onFooterClick,
  title,
}: {
  children: ReactNode;
  footer: ReactNode;
  footerDisabled?: boolean;
  footerHref?: string;
  isLoadingFooter?: boolean;
  onFooterClick?: () => void;
  title: string;
}): ReactNode {
  const isFooterDisabled = footerDisabled || isLoadingFooter;
  const footerClassName = isFooterDisabled ? cardFooterDisabledClassName : cardFooterClassName;
  const footerContent = isLoadingFooter ? (
    <span aria-hidden className="block h-4 w-32 animate-pulse rounded bg-white/10" />
  ) : (
    <>
      {footer}
      <FaChevronRight size={12} />
    </>
  );

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-bzgray-800 bg-[#122135]">
      <div className="shrink-0 px-4 pb-1.5 pt-3 text-base font-bold uppercase leading-5 text-bzgray-300">{title}</div>
      <div className="relative flex h-[9.5rem] max-h-[9.5rem] min-h-[9.5rem] shrink-0 flex-col overflow-hidden">
        {children}
      </div>
      {footerHref && !isFooterDisabled ? (
        <Link className={footerClassName} href={footerHref} rel="noopener noreferrer" target="_blank">
          {footerContent}
        </Link>
      ) : (
        <button className={footerClassName} disabled={isFooterDisabled} onClick={onFooterClick} type="button">
          {footerContent}
        </button>
      )}
    </div>
  );
}

export function HeaderCardList({ children }: { children: ReactNode }): ReactNode {
  return <div className="flex h-full flex-1 flex-col justify-start overflow-hidden px-4 pb-2">{children}</div>;
}

export function HeaderCardEmpty({
  description,
  icon: Icon,
  title,
}: {
  description: string;
  icon?: IconType;
  title: string;
}): ReactNode {
  if (Icon) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-4 pb-2 text-center" role="status">
        <span className="flex size-9 items-center justify-center rounded-full bg-white/5 text-bzgray-400">
          <Icon size={16} />
        </span>
        <p className={emptyTitleClassName}>{title}</p>
        <p className={emptyCopyClassName}>{description}</p>
      </div>
    );
  }

  return (
    <div
      className="flex h-full max-w-[36ch] flex-1 flex-col justify-center gap-2 overflow-hidden px-4 py-2"
      role="status"
    >
      <p className={emptyTitleClassName}>{title}</p>
      <p className={emptyCopyClassName}>{description}</p>
    </div>
  );
}

export function HeaderCardRow({ children, href }: { children: ReactNode; href?: string }): ReactNode {
  if (href) {
    return (
      <a className={cardRowClassName} href={href}>
        {children}
      </a>
    );
  }

  return <div className={cardRowClassName}>{children}</div>;
}

export function HeaderTickerPrice({
  percent,
  positive,
  price,
}: {
  percent: ReactNode;
  positive: boolean;
  price?: ReactNode;
}): ReactNode {
  return (
    <div
      className={`flex shrink-0 flex-row items-center justify-self-end gap-2 text-base font-bold ${
        positive ? 'text-bzgreen-500' : 'text-bzred-500'
      }`}
    >
      {positive ? <FaCaretUp /> : <FaCaretDown />}
      {price ? <span>{price}</span> : null}
      <span className={`rounded-3xl px-2 text-sm ${positive ? 'bg-bzgreen-500/10' : 'bg-bzred-500/10'}`}>
        {percent}
      </span>
    </div>
  );
}
